import * as React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Alert, Box, Button, Skeleton, Stack, Typography } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useChargebackDispute } from '../../../../../core/hooks/useChargebackDispute'
import DisputeStatusChip from '../../../../../components/Disputes/DisputeStatusChip'
import DisputeDetailContent from '../../../../../components/Disputes/DisputeDetailContent'
import DisputeEvidenceDialog from '../../../../../components/Disputes/DisputeEvidenceDialog'

export interface OrderDetailDisputeProps {
  orderChargeId?: string | null
}

export default function OrderDetailDispute({
  orderChargeId,
}: OrderDetailDisputeProps) {
  const [contestOpen, setContestOpen] = React.useState(false)
  const { loading, data, error, notFound, execute } = useChargebackDispute({
    orderChargeId: orderChargeId || undefined,
  })

  if (!orderChargeId) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">Cobrança não identificada</Alert>
      </Box>
    )
  }

  if (loading && !data) {
    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <Skeleton variant="rounded" height={40} />
        <Skeleton variant="rounded" height={160} />
        <Skeleton variant="rounded" height={120} />
      </Stack>
    )
  }

  if (notFound) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Disputa não encontrada</Alert>
      </Box>
    )
  }

  // 400: gateway sem suporte (Stripe/Asaas) ou disputa ainda não sincronizada
  if (error !== '') {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">{error}</Alert>
      </Box>
    )
  }

  if (!data) return null

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            Disputa #{data.disputeId}
          </Typography>
          <DisputeStatusChip status={data.status} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            component={RouterLink}
            to={`/disputes/${data.id}`}
            endIcon={<OpenInNewIcon fontSize="small" />}
          >
            Abrir disputa
          </Button>
          {data.canSubmitEvidence && (
            <Button
              size="small"
              variant="contained"
              onClick={() => setContestOpen(true)}
            >
              Contestar
            </Button>
          )}
        </Stack>
      </Stack>

      <DisputeDetailContent dispute={data} hideSale />

      <DisputeEvidenceDialog
        open={contestOpen}
        disputeId={data.id}
        onClose={() => setContestOpen(false)}
        onSent={execute}
      />
    </Stack>
  )
}
